import type { HttpErrorType, WhenError } from '../request/http';

/** 登录失败默认文案 key */
const DEFAULT_LOGIN_FAILED_KEY = 'account_manager_login_failed';

/** 带 HTTP 状态的错误体 */
type LoginErrorPayload = WhenError & { status?: number; result?: HttpErrorType };

/** 服务端/网络类英文片段 */
const SERVER_OR_NETWORK_TEXT_PATTERNS = [
  'service is not available',
  'database unavailable',
  'network error',
  'network request',
  'timeout',
  'econnrefused',
  'err_network',
  'failed to fetch',
] as const;

/** 账号/密码类英文 details */
const CREDENTIAL_TEXT_PATTERNS = [
  'password is incorrect',
  'account not found',
  'does not exist',
  'account resource deleted',
  'account has not been verified',
  'account no password',
  'verification failed',
  'limit login times',
  'logins per day',
  'please check the account type',
  'resource not found',
] as const;

/**
 * 从接口错误对象中收集可用于匹配的英文详情/消息文本。
 * @param error - 登录接口返回体或纯字符串
 * @returns 合并后的检索串
 */
function collectLoginErrorText(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (!error || typeof error !== 'object') {
    return '';
  }
  const parts: string[] = [];
  const payload = error as LoginErrorPayload;
  if (typeof payload.message === 'string' && payload.message) {
    parts.push(payload.message);
  }
  const details = payload.result?.details;
  if (typeof details === 'string') {
    parts.push(details);
  } else if (details != null) {
    parts.push(JSON.stringify(details));
  }
  if (typeof payload.result?.message === 'string' && payload.result.message) {
    parts.push(payload.result.message);
  }
  const code = payload.result?.code;
  if (code != null) {
    parts.push(String(code));
  }
  return parts.join(' ');
}

/**
 * 读取 HTTP 状态码。
 * @param error - 登录错误体
 */
function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const status = (error as LoginErrorPayload).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * 是否为服务端异常或网络不可用。
 * @param error - 登录错误体
 * @param text - 合并后的英文检索串
 */
function isServerOrNetworkError(error: unknown, text: string): boolean {
  const status = getHttpStatus(error);
  if (status != null && status >= 500) {
    return true;
  }
  const code = (error as LoginErrorPayload)?.result?.code;
  if (code === 1005 || code === 1006) {
    return true;
  }
  const lower = text.toLowerCase();
  return SERVER_OR_NETWORK_TEXT_PATTERNS.some((p) => lower.includes(p));
}

/**
 * 是否为账号/密码类鉴权失败。
 * @param error - 登录错误体
 * @param text - 合并后的英文检索串
 */
function isCredentialError(error: unknown, text: string): boolean {
  const lower = text.toLowerCase();
  if (CREDENTIAL_TEXT_PATTERNS.some((p) => lower.includes(p))) {
    return true;
  }
  const status = getHttpStatus(error);
  return status === 401 || status === 403 || status === 404;
}

/**
 * 将登录相关后端错误映射为 i18n key（与 App `map-login-error-to-i18n-key` 规则一致）。
 *
 * @param error - 登录 token 接口错误体
 * @param defaultKey - 无法识别时的 i18n key
 * @returns 供 `i18n.t()` 使用的 key
 */
export function mapLoginErrorToI18nKey(
  error: unknown,
  defaultKey: string = DEFAULT_LOGIN_FAILED_KEY
): string {
  const text = collectLoginErrorText(error);

  if (text.includes('Login is temporarily blocked for this IP')) {
    return 'account_manager_login_failed_blocked_ip';
  }

  if (isServerOrNetworkError(error, text)) {
    const lower = text.toLowerCase();
    if (lower.includes('timeout') || lower.includes('network')) {
      return 'http_message_network_request_timed_out';
    }
    return 'request_failure_text_service_is_not_available';
  }

  if (isCredentialError(error, text)) {
    return 'login_page_alert_wrong_account_or_password';
  }

  if (!text) {
    return defaultKey;
  }

  return defaultKey;
}
